/**
 * Placeholders shown while Hermes data loads, in the shape of what is coming:
 * the conversation, the chat history list and the full results table. They hold
 * the layout still so the content does not jump when it arrives.
 */

export function SkeletonBar({ className = "" }: { className?: string }) {
  return <div aria-hidden="true" className={`tw-rounded tw-bg-gray-200 tw-animate-pulse ${className}`} />;
}

/** An opened conversation while its messages load: the question, an answer with a table, more text. */
export function ConversationSkeleton() {
  return (
    <div role="status" aria-label="Loading conversation" className="tw-h-full tw-overflow-hidden">
      <div className="tw-w-full tw-max-w-[790px] tw-mx-auto tw-box-border tw-pt-8 tw-flex tw-flex-col tw-gap-8">
        <div className="tw-flex tw-justify-end">
          <SkeletonBar className="tw-h-9 tw-w-2/5 tw-rounded-2xl" />
        </div>

        <div className="tw-flex tw-flex-col tw-gap-3">
          <SkeletonBar className="tw-h-4 tw-w-full" />
          <SkeletonBar className="tw-h-4 tw-w-11/12" />
          <SkeletonBar className="tw-h-4 tw-w-2/3" />
        </div>

        <div className="tw-flex tw-flex-col tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-gray-200 tw-p-3">
          <SkeletonBar className="tw-h-4 tw-w-full tw-bg-gray-300" />
          {Array.from({ length: 5 }, (_, row) => (
            <SkeletonBar key={row} className="tw-h-4 tw-w-full" />
          ))}
        </div>

        <div className="tw-flex tw-flex-col tw-gap-3">
          <SkeletonBar className="tw-h-4 tw-w-1/4 tw-bg-gray-300" />
          <SkeletonBar className="tw-h-4 tw-w-full" />
          <SkeletonBar className="tw-h-4 tw-w-5/6" />
        </div>
      </div>
    </div>
  );
}

/** The chat history list while it loads. */
export function HistoryListSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <ul role="status" aria-label="Loading conversations" className="tw-list-none tw-p-0 tw-m-0 tw-flex tw-flex-col tw-gap-3">
      {Array.from({ length: rows }, (_, row) => (
        <li key={row} className="tw-px-2">
          <SkeletonBar className={`tw-h-4 ${row % 3 === 0 ? "tw-w-11/12" : row % 3 === 1 ? "tw-w-3/4" : "tw-w-5/6"}`} />
        </li>
      ))}
    </ul>
  );
}

/** The full results table while its first page loads: a checkbox column, a serial column and `columns` data columns. */
export function TableSkeleton({ columns = 5, rows = 12 }: { columns?: number; rows?: number }) {
  return (
    <table role="status" aria-label="Loading records" className="tw-w-full tw-border-separate tw-border-spacing-0">
      <thead className="tw-bg-gray-50">
        <tr>
          {Array.from({ length: columns + 2 }, (_, column) => (
            <th key={column} className="tw-px-3 tw-py-3 tw-border-0 tw-border-b tw-border-solid tw-border-gray-200">
              <SkeletonBar className={`tw-h-3 ${column === 0 ? "tw-w-4" : column === 1 ? "tw-w-6" : "tw-w-20"} tw-bg-gray-300`} />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }, (_, row) => (
          <tr key={row}>
            {Array.from({ length: columns + 2 }, (_, column) => (
              <td key={column} className="tw-px-3 tw-py-3.5 tw-border-0 tw-border-b tw-border-solid tw-border-gray-100">
                <SkeletonBar className={`tw-h-3.5 ${column === 0 ? "tw-w-4" : column === 1 ? "tw-w-6" : column === 2 ? "tw-w-40" : "tw-w-16"}`} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
